import {
    autoUpdate,
    flip,
    offset,
    shift,
    useFloating,
} from "@floating-ui/react";
import {
    faArrowsRotate,
    faChevronDown,
    faSatelliteDish,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Popover } from "@headlessui/react";
import { useQuery } from "@tanstack/react-query";
import type { AdminEmissionsRecordsStatsListProviderWithSourceParameterInner } from "../../../apiClient/generated";
import { useDataProvidersApi } from "../../../hooks";
import { MapProviderFilter } from "../../filters/ProviderFilter";

interface ProviderFilterWrapperProps {
    filterValue?:
        | AdminEmissionsRecordsStatsListProviderWithSourceParameterInner[]
        | "all";
    setFilterValue: (
        value?:
            | AdminEmissionsRecordsStatsListProviderWithSourceParameterInner[]
            | "all",
    ) => void;
}

export const ProviderFilterWrapper = ({
    filterValue,
    setFilterValue,
}: ProviderFilterWrapperProps) => {
    const isDefault = !filterValue;
    const apiClient = useDataProvidersApi();
    const dataProvidersQuery = useQuery({
        queryKey: ["analyticsDataProviders"],
        queryFn: async () => {
            return await apiClient.dataProvidersList();
        },
        refetchOnWindowFocus: false,
        staleTime: Infinity,
    });

    // Floating UI setup for popover positioning
    const { refs, floatingStyles } = useFloating({
        strategy: "fixed",
        placement: "bottom-start",
        middleware: [
            offset({
                mainAxis: 4,
            }),
            shift(),
            flip(),
        ],
        whileElementsMounted: autoUpdate,
    });

    const dataProviders = dataProvidersQuery.data?.results || [];

    // Calculate display text for button
    const getDisplayText = () => {
        if (!filterValue || filterValue === "all") {
            return "All providers";
        }
        if (Array.isArray(filterValue)) {
            if (filterValue.length === 0) {
                return "All providers";
            }
            if (filterValue.length === 1) {
                const provider = dataProviders.find(
                    (p) => p.id === filterValue[0].id,
                );
                return provider?.name || "1 provider";
            }
            return `${filterValue.length} providers`;
        }
        return "All providers";
    };

    return (
        <div ref={refs.setReference}>
            <Popover>
                {({ close }) => (
                    <>
                        <Popover.Button
                            className={`
                                flex items-center gap-2 px-3 h-8 text-sm border
                                rounded  hover:bg-ae-gray-100
                                ${isDefault ? "border-ae-gray-250 bg-white" : "border-ae-blue-550 bg-ae-blue-100"}
                            `}
                        >
                            <FontAwesomeIcon
                                icon={faSatelliteDish}
                                className="w-4 text-ae-blue-550"
                            />
                            <span className="text-neutral-600 hidden xl:block whitespace-nowrap">
                                {getDisplayText()}
                            </span>
                            {dataProvidersQuery.isLoading ? (
                                <div className="animate-spin">
                                    <FontAwesomeIcon
                                        icon={faArrowsRotate}
                                        className="w-3"
                                    />
                                </div>
                            ) : (
                                <FontAwesomeIcon
                                    icon={faChevronDown}
                                    className="w-3"
                                />
                            )}
                        </Popover.Button>
                        <Popover.Panel
                            ref={refs.setFloating}
                            style={floatingStyles}
                            onKeyUp={(key) => {
                                if (key.code === "Enter") {
                                    close();
                                }
                            }}
                            className="w-[380px] overflow-auto rounded-md p-1 bg-white py-1 border-2 border-ae-slate-300 z-50 drop-shadow-lg"
                        >
                            {dataProvidersQuery.isLoading ? (
                                <div className="flex items-center justify-center p-4">
                                    <FontAwesomeIcon
                                        icon={faArrowsRotate}
                                        className="w-5 animate-spin text-ae-blue-550"
                                    />
                                </div>
                            ) : (
                                <MapProviderFilter
                                    filterValue={filterValue || []}
                                    setFilterValue={(value) => {
                                        // Convert empty array back to undefined for consistency
                                        if (
                                            Array.isArray(value) &&
                                            value.length === 0
                                        ) {
                                            setFilterValue(undefined);
                                        } else {
                                            setFilterValue(value);
                                        }
                                    }}
                                    dataProviders={dataProviders}
                                />
                            )}
                        </Popover.Panel>
                    </>
                )}
            </Popover>
        </div>
    );
};
